import { css } from "@emotion/react"
import { forwardRef, useLayoutEffect, useRef, useState } from "react"
import { Field } from "source/giving/components/Field"
import { Amount, ValidationErrorMessage } from "../../types"

interface BigCurrencyInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "value"> {
  amount: Amount
  errors?: ValidationErrorMessage[]
}

export const BigCurrencyInput = forwardRef(function BigCurrencyInput(
  {
    amount,
    onChange,
    readOnly = false,
    onBlur,
    errors,
    ...inputProps
  }: BigCurrencyInputProps,
  ref: React.ForwardedRef<HTMLInputElement>,
) {
  const CURRENCY_SYMBOL = "$"
  const placeholder = "0"

  const [inputWidth, setInputWidth] = useState("auto")
  const mirrorRef = useRef<HTMLSpanElement | null>(null)

  useLayoutEffect(() => {
    if (mirrorRef.current) {
      mirrorRef.current.textContent = amount || placeholder
      setInputWidth(`${mirrorRef.current.offsetWidth}px`)
    }
  }, [amount])

  return (
    <Field errors={errors} alignErrors="center">
      {({ id, isValid, errorId }) => (
        <div className="d-f jc-c">
          <span className="fs-0 c-tint3 pt-1">{CURRENCY_SYMBOL}</span>
          <div
            className="p-r d-g o-h"
            css={css`
              max-width: 100%;
            `}
          >
            <input
              {...inputProps}
              id={id}
              type="text"
              inputMode="decimal"
              placeholder={placeholder}
              value={amount}
              onChange={onChange}
              onBlur={onBlur}
              readOnly={readOnly}
              maxLength={10}
              css={[styles.font, styles.input]}
              style={{ width: inputWidth }}
              ref={ref}
              aria-invalid={!isValid}
              aria-describedby={errorId}
              data-testid="total-amount-input"
            />
            <span
              aria-hidden="true"
              className="p-a v-h pe-n"
              css={styles.font}
              ref={mirrorRef}
            >
              {amount || placeholder}
            </span>
          </div>
        </div>
      )}
    </Field>
  )
})

const styles = {
  font: css`
    &,
    &[type="text"] {
      font: inherit;
      font-size: 72px;
      padding-inline: 2px;
    }
  `,
  input: css`
    // add specificity to override doxy defaults
    &[type="text"] {
      grid-area: 1 / 1;
      height: auto;
      line-height: 1;
      padding-block: 0;
      border: none;
      border-bottom: 2px solid var(--color-tint5);
      border-radius: 0 !important;
      box-shadow: none;
      min-width: calc(1ch + 0.25rem);

      &[readonly] {
        background-color: transparent;
        cursor: not-allowed;

        &:active,
        &:focus {
          border-color: transparent;
        }
      }

      &:active,
      &:focus {
        border-color: var(--color-tint2);
        background-color: transparent;
        outline: none;
        box-shadow: none;
      }
    }
  `,
}
